/**
 * NUKIA — theme.js
 * Ported from the original Vite build (main/shop/product/cart .js)
 * with the static sessionStorage cart swapped for Shopify's Ajax Cart API.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     CART — Shopify Ajax API
     ───────────────────────────────────────────────────────── */
  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('.nav-cart-link').forEach((link) => {
        link.textContent = `Cart (${cart.item_count})`;
      });
      document.querySelectorAll('.nav-cart-count').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.classList.toggle('is-empty', cart.item_count === 0);
      });
      return cart;
    } catch (e) { /* offline / non-shopify preview — leave as-is */ }
  }

  function pulseCartIcon() {
    document.querySelectorAll('.nav-cart-icon').forEach((icon) => {
      icon.classList.remove('pulse');
      // force reflow so the animation can restart on back-to-back adds
      void icon.offsetWidth;
      icon.classList.add('pulse');
      setTimeout(() => icon.classList.remove('pulse'), 900);
    });
  }

  async function addToCart(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    await refreshCartCount();
    pulseCartIcon();
    return res.json();
  }

  /* ─────────────────────────────────────────────────────────
     1. SCROLL REVEAL ENGINE
     ───────────────────────────────────────────────────────── */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    reveals.forEach((el) => {
      const parent = el.parentElement;
      if (!parent) return;
      const siblings = [...parent.querySelectorAll(':scope > .reveal')];
      const idx = siblings.indexOf(el);
      if (idx > 0) el.style.setProperty('--delay', `${idx * 80}ms`);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );
    reveals.forEach((el) => observer.observe(el));
  }

  /* ─────────────────────────────────────────────────────────
     2. NAV — Contextual scroll behavior
     ───────────────────────────────────────────────────────── */
  function initNav() {
    const nav = document.getElementById('nav');
    if (!nav) return;
    const isIndex = nav.dataset.index === 'true';
    const heroEl = document.getElementById('hero');

    function updateNav() {
      const scrollY = window.scrollY;
      nav.classList.toggle('scrolled', scrollY > 40);
      if (isIndex) {
        const heroHeight = heroEl ? heroEl.offsetHeight : 0;
        nav.classList.toggle('on-light', scrollY > heroHeight - 80);
      }
    }
    window.addEventListener('scroll', updateNav, { passive: true });
    updateNav();

    // Mobile hamburger menu
    const toggle = document.getElementById('navToggle');
    if (toggle) {
      toggle.addEventListener('click', () => {
        const open = nav.classList.toggle('menu-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      // Close the menu when a link inside it is tapped
      nav.querySelectorAll('.nav-links a').forEach((a) => {
        a.addEventListener('click', () => {
          nav.classList.remove('menu-open');
          toggle.setAttribute('aria-expanded', 'false');
        });
      });
    }
  }

  /* ─────────────────────────────────────────────────────────
     3. MOBILE — Tap-to-reveal for bottle hover panels
     ───────────────────────────────────────────────────────── */
  function initMobileTap() {
    const bottleWraps = document.querySelectorAll('.scent-bottle-wrap');
    if (!bottleWraps.length) return;
    bottleWraps.forEach((wrap) => {
      wrap.addEventListener('touchstart', () => {
        const isOpen = wrap.classList.contains('tapped');
        bottleWraps.forEach((w) => w.classList.remove('tapped'));
        if (!isOpen) wrap.classList.add('tapped');
      }, { passive: true });
    });
    document.addEventListener('touchstart', (e) => {
      if (!e.target.closest('.scent-bottle-wrap')) {
        bottleWraps.forEach((w) => w.classList.remove('tapped'));
      }
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────
     4. SMOOTH ANCHOR LINKS
     ───────────────────────────────────────────────────────── */
  function initSmoothAnchors() {
    document.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('href').slice(1);
        if (!targetId) return;
        const target = document.getElementById(targetId);
        const nav = document.querySelector('.nav');
        if (!target) return;
        e.preventDefault();
        const navH = nav ? nav.offsetHeight : 0;
        const top = target.getBoundingClientRect().top + window.scrollY - navH - 16;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     5. MAGNETIC IMAGES (desktop hover)
     ───────────────────────────────────────────────────────── */
  function initMagneticImages() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    document.querySelectorAll('.scent-bottle-img').forEach((img) => {
      const wrap = img.closest('.scent-bottle-wrap');
      if (!wrap) return;
      wrap.addEventListener('mousemove', (e) => {
        const rect = wrap.getBoundingClientRect();
        const dx = (e.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
        const dy = (e.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
        img.style.transform = `scale(1.06) translateY(-16px) rotateX(${dy * -3}deg) rotateY(${dx * 3}deg)`;
        img.style.transition = 'transform 0.12s linear';
      });
      wrap.addEventListener('mouseleave', () => {
        img.style.transform = '';
        img.style.transition = '';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     6. HERO SCROLL PARALLAX
     ───────────────────────────────────────────────────────── */
  function initHeroParallax() {
    const heroBgImg = document.querySelector('.hero-bg img');
    if (!heroBgImg) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        if (scrollY < window.innerHeight) {
          heroBgImg.style.transform = `scale(1.04) translateY(${scrollY * 0.28}px)`;
        }
        ticking = false;
      });
      ticking = true;
    }, { passive: true });
  }

  /* ─────────────────────────────────────────────────────────
     7. COLLECTION HOVER GRACE PERIOD
     ───────────────────────────────────────────────────────── */
  function initCollectionHoverGrace() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    document.querySelectorAll('.scent-bottle-wrap').forEach((wrap) => {
      const panel = wrap.querySelector('.scent-hover-panel');
      let hideTimer;
      const activate = () => { clearTimeout(hideTimer); wrap.classList.add('hovered'); };
      const scheduleDeactivate = () => {
        hideTimer = setTimeout(() => wrap.classList.remove('hovered'), 350);
      };
      wrap.addEventListener('mouseenter', activate);
      wrap.addEventListener('mouseleave', scheduleDeactivate);
      if (panel) {
        panel.addEventListener('mouseenter', activate);
        panel.addEventListener('mouseleave', scheduleDeactivate);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     8. SHOP — quick add from grid tiles
     ───────────────────────────────────────────────────────── */
  function initShopQuickAdd() {
    document.querySelectorAll('.tile-btn-add[data-variant-id]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;
        const original = btn.textContent;
        btn.disabled = true;
        try {
          await addToCart(btn.dataset.variantId, 1);
          btn.textContent = 'Added';
        } catch (err) {
          btn.textContent = 'Error';
        }
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1400);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     9. SHOP — filters
     ───────────────────────────────────────────────────────── */
  function initShopFilters() {
    const filterBtns = document.querySelectorAll('.filter-btn');
    const tiles = document.querySelectorAll('.product-tile');
    if (!filterBtns.length) return;
    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = btn.dataset.filter;
        tiles.forEach((tile) => {
          const hidden = filter !== 'all' && tile.dataset.category !== filter;
          tile.classList.toggle('hidden', hidden);
        });
        const visible = [...tiles].filter((t) => !t.classList.contains('hidden'));
        visible.forEach((t, i) => { t.style.transitionDelay = `${i * 60}ms`; });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────
     10. PRODUCT PAGE — qty, variants, accordions, add-to-cart
     ───────────────────────────────────────────────────────── */
  function initProductPage() {
    // Image gallery: horizontal swipe/scroll strip, thumbnails jump to a slide
    const scrollWrap = document.getElementById('pdpImageScroll');
    const thumbs = document.getElementById('pdpThumbs');
    const slides = scrollWrap ? Array.from(scrollWrap.querySelectorAll('.pdp-image-slide')) : [];

    if (scrollWrap && slides.length) {
      const setActiveThumb = (idx) => {
        if (!thumbs) return;
        thumbs.querySelectorAll('.pdp-thumb').forEach((b) => {
          b.classList.toggle('active', parseInt(b.dataset.index, 10) === idx);
        });
      };

      if (thumbs) {
        thumbs.querySelectorAll('.pdp-thumb').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.index, 10);
            const target = slides[idx];
            if (target) {
              scrollWrap.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
              setActiveThumb(idx);
            }
          });
        });
      }

      // Keep the active thumbnail in sync while the user swipes/scrolls
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              setActiveThumb(parseInt(entry.target.dataset.index, 10));
            }
          });
        },
        { root: scrollWrap, threshold: [0.6] }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    const form = document.getElementById('pdpForm');

    // Accordions (present on every PDP)
    document.querySelectorAll('.pdp-accordion-item').forEach((item) => {
      const trigger = item.querySelector('.pdp-accordion-trigger');
      const icon = item.querySelector('.pdp-accordion-icon');
      if (item.dataset.open === 'true') item.classList.add('open');
      if (icon) icon.textContent = item.classList.contains('open') ? '−' : '+';
      if (trigger) {
        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('open');
          item.classList.toggle('open', !isOpen);
          if (icon) icon.textContent = !isOpen ? '−' : '+';
        });
      }
    });

    // Animate the (open) notes accord bars in
    const accords = document.getElementById('pdpAccords');
    if (accords) {
      requestAnimationFrame(() => requestAnimationFrame(() => accords.classList.add('visible')));
    }

    if (!form) return;

    // Quantity selector
    const qtyInput = document.getElementById('qtyInput');
    const qtyMinus = document.getElementById('qtyMinus');
    const qtyPlus = document.getElementById('qtyPlus');
    const MIN = 1, MAX = 10;
    function setQty(n) {
      const c = Math.min(MAX, Math.max(MIN, n || MIN));
      qtyInput.value = c;
      if (qtyMinus) qtyMinus.disabled = c <= MIN;
      if (qtyPlus) qtyPlus.disabled = c >= MAX;
    }
    if (qtyInput) {
      if (qtyMinus) qtyMinus.addEventListener('click', () => setQty(parseInt(qtyInput.value, 10) - 1));
      if (qtyPlus) qtyPlus.addEventListener('click', () => setQty(parseInt(qtyInput.value, 10) + 1));
      setQty(1);
    }

    // Variant select → update price + availability
    const variantSelect = document.getElementById('variantSelect');
    const priceSale = document.getElementById('pdpPriceSale');
    const addBtn = document.getElementById('addToCartBtn');
    const addLabel = addBtn ? addBtn.querySelector('.pdp-add-label') : null;
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        const opt = variantSelect.options[variantSelect.selectedIndex];
        if (priceSale && opt.dataset.price) priceSale.textContent = opt.dataset.price;
        if (addBtn) {
          addBtn.disabled = opt.disabled;
          if (addLabel) addLabel.textContent = opt.disabled ? 'Sold Out' : 'Add to Cart';
        }
      });
    }

    // Add to cart (Ajax, with graceful confirmation)
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!addBtn || addBtn.disabled) return;
      const variantId = variantSelect ? variantSelect.value : form.querySelector('[name="id"]').value;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      addBtn.disabled = true;
      try {
        await addToCart(variantId, qty);
        addBtn.classList.add('added');
        if (addLabel) addLabel.textContent = qty > 1 ? `Added ${qty} to Cart` : 'Added to Cart';
        setTimeout(() => {
          addBtn.classList.remove('added');
          if (addLabel) addLabel.textContent = 'Add to Cart';
          addBtn.disabled = false;
          setQty(1);
        }, 1800);
      } catch (err) {
        if (addLabel) addLabel.textContent = 'Unavailable';
        setTimeout(() => {
          if (addLabel) addLabel.textContent = 'Add to Cart';
          addBtn.disabled = false;
        }, 1800);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────
     INIT
     ───────────────────────────────────────────────────────── */
  function init() {
    initNav();
    initScrollReveal();
    initMobileTap();
    initSmoothAnchors();
    initMagneticImages();
    initHeroParallax();
    initCollectionHoverGrace();
    initShopQuickAdd();
    initShopFilters();
    initProductPage();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
